import { hasInvoiceFilters, invoiceTableState, invoicesPath } from './invoiceListFilters'
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import {
  ListPager,
  STAFF_LIST_PAGE_SIZE,
  totalDescription,
  useCursorPages,
} from '@/components/templates/list-pager'
import { AccountingSyncBadge } from '@/components/AccountingSyncBadge'
import { OwnedChip } from '@/components/OwnedChip'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getJSON } from '@/lib/api'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { displayDate } from '@/lib/dates'
import { InvoiceDetailPage } from '@/pages/InvoiceDetailPage'
import { ManualInvoiceDialog, submitInvoiceForm } from '@/pages/ManualInvoiceDialog'
import { invoiceStatusVariant } from '@/pages/InvoicesPage.status'
import type { IncomeInvoiceDrillDown } from '@/lib/incomeInvoiceDrillDown'
import type { AgingContactDrillDown } from '@/pages/AgingReportPage'
import type {
  ContactsResponse,
  Invoice,
  InvoiceFilters,
  InvoiceOriginFilter,
  InvoicesResponse,
  InvoiceStatusFilter,
} from '@/pages/InvoicesPage.types'
import type { TaxRatesResponse } from '@/pages/TaxSettingsPage'
import { INVOICE_ORIGINS } from '@/pages/InvoicesPage.types'

export { InvoiceDetailPage }
export type { Invoice } from '@/pages/InvoicesPage.types'

export interface InvoicesPageProps {
  currentAssociationDate?: string
  onOpenInvoice: (invoice: Invoice) => void
  drillDown?: IncomeInvoiceDrillDown
  agingContact?: AgingContactDrillDown
}

const invoiceColumns: ListPageColumn<Invoice>[] = [
  {
    id: 'number',
    header: 'Invoice',
    cell: (invoice) => (
      <div className="flex flex-col">
        <span className="flex items-center gap-2 font-medium text-foreground">
          #{invoice.number}
          <OwnedChip owner={invoice.owner} />
        </span>
        <span className="text-caption text-muted-foreground">{invoice.origin}</span>
      </div>
    ),
  },
  {
    id: 'contact',
    header: 'Contact',
    cell: (invoice) => <span>{invoice.contact_name}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: (invoice) => (
      <Badge variant={invoiceStatusVariant[invoice.status]}>{invoice.status}</Badge>
    ),
  },
  {
    id: 'dates',
    header: 'Dates',
    cell: (invoice) => (
      <div className="flex flex-col text-muted-foreground">
        <span>{displayDate(invoice.issue_on)}</span>
        <span className="text-caption">Due {displayDate(invoice.due_on)}</span>
      </div>
    ),
  },
  {
    id: 'accounting',
    header: 'Accounting',
    cell: (invoice) => (
      <AccountingSyncBadge
        state={invoice.accounting_sync_state ?? null}
        detail={invoice.accounting_sync_detail}
      />
    ),
  },
  {
    id: 'total',
    header: 'Total',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (invoice) => invoice.total,
  },
  {
    id: 'balance',
    header: 'Balance',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    cell: (invoice) => invoice.balance,
  },
]

export function InvoicesPage({
  currentAssociationDate,
  onOpenInvoice,
  drillDown,
  agingContact,
}: InvoicesPageProps) {
  const queryClient = useQueryClient()
  const [filters, setFilters] = React.useState<InvoiceFilters>({
    status: 'all',
    origin: 'all',
    issuedFrom: '',
    issuedThrough: '',
    openBalance: false,
  })
  const [ownerIds, setOwnerIds] = React.useState<Set<string>>(new Set())
  const [q, setQ] = React.useState('')
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const { query: invoicesQuery, pager } = useCursorPages<InvoicesResponse>({
    queryKey: ['invoices'],
    path: invoicesPath(filters, drillDown, agingContact, ownerIds, q),
    limit: STAFF_LIST_PAGE_SIZE,
  })
  const contactsQuery = useQuery({
    queryKey: ['contacts', 'invoice-form'],
    queryFn: () => getJSON<ContactsResponse>('/api/v1/contacts'),
    retry: false,
    enabled: dialogOpen && !drillDown && !agingContact,
  })
  const connectionsQuery = useQuery({
    queryKey: ['connections', 'invoice-owner-filter'],
    queryFn: () =>
      getJSON<{ connections: Array<{ id: number; display_name: string }> }>('/api/v1/connections'),
    retry: false,
  })
  const taxRatesQuery = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => getJSON<TaxRatesResponse>('/api/v1/tax_rates'),
    retry: false,
    enabled: dialogOpen,
  })
  const createInvoice = useMutation({
    mutationFn: submitInvoiceForm,
    onSuccess: async () => {
      setDialogOpen(false)
      await queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
  const hasFilters = hasInvoiceFilters(filters, ownerIds) || q.trim() !== ''
  const presentation = invoiceListPresentation({
    drillDown,
    agingContact,
    filters,
    setFilters,
    setDialogOpen,
    ownerIds,
    connections: connectionsQuery.data?.connections ?? [],
    setOwnerIds,
  })

  return (
    <>
      <ListPage<Invoice>
        title="Invoices"
        description={totalDescription(
          invoicesQuery.data?.total_count,
          'Invoice',
          'Invoices',
          hasFilters,
        )}
        search={{ value: q, onChange: setQ, placeholder: 'Search invoice, contact or event' }}
        primaryAction={presentation.primaryAction}
        toolbar={presentation.toolbar}
        table={{
          rows: invoicesQuery.data?.invoices ?? [],
          columns: invoiceColumns,
          getRowId: (invoice) => invoice.id,
          getRowLabel: (invoice) => `Invoice #${invoice.number}`,
          onRowOpen: onOpenInvoice,
          state: invoiceTableState(invoicesQuery.isLoading, invoicesQuery.isError),
          loadingTitle: 'Loading Invoices',
          emptyTitle: presentation.emptyTitle(hasFilters),
          errorTitle: 'Invoices unavailable',
          errorDescription: 'Refresh and try again.',
          header: <ListPager pager={pager} label="Invoices" position="top" />,
          footer: <ListPager pager={pager} label="Invoices" position="bottom" />,
        }}
      />

      {dialogOpen && (
        <ManualInvoiceDialog
          contacts={contactsQuery.data?.contacts ?? []}
          contactsLoading={contactsQuery.isLoading}
          taxRates={taxRatesQuery.data?.tax_rates ?? []}
          taxRatesLoading={taxRatesQuery.isLoading}
          pending={createInvoice.isPending}
          error={createInvoice.error}
          currentAssociationDate={currentAssociationDate}
          onClose={() => setDialogOpen(false)}
          onSubmit={(form) => createInvoice.mutate(form)}
        />
      )}
    </>
  )
}

function invoiceListPresentation({
  drillDown,
  agingContact,
  filters,
  setFilters,
  setDialogOpen,
  ownerIds,
  connections,
  setOwnerIds,
}: {
  drillDown: IncomeInvoiceDrillDown | undefined
  agingContact: AgingContactDrillDown | undefined
  filters: InvoiceFilters
  setFilters: React.Dispatch<React.SetStateAction<InvoiceFilters>>
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
  ownerIds: ReadonlySet<string>
  connections: Array<{ id: number; display_name: string }>
  setOwnerIds: React.Dispatch<React.SetStateAction<Set<string>>>
}) {
  if (drillDown) {
    return {
      primaryAction: undefined,
      toolbar: <IncomeDrillDownSummary drillDown={drillDown} />,
      emptyTitle: () => 'No matching Invoices',
    }
  }
  if (agingContact) {
    return {
      primaryAction: undefined,
      toolbar: (
        <p className="text-body-sm text-muted-foreground">
          {agingContact.contactName} · open Invoices
        </p>
      ),
      emptyTitle: () => 'No open Invoices',
    }
  }

  return {
    primaryAction: { label: 'New invoice', onClick: () => setDialogOpen(true) },
    toolbar: (
      <>
        <InvoicesToolbar filters={filters} onChange={setFilters} />
        <FilterDropdown
          label="Owner"
          emptyLabel="All owners"
          options={[
            { value: 'bright_huddle', label: 'Bright Huddle' },
            ...connections.map((connection) => ({
              value: String(connection.id),
              label: connection.display_name,
            })),
          ]}
          selected={ownerIds}
          onChange={(selected) => {
            const last = Array.from(selected).at(-1)
            setOwnerIds(last ? new Set([last]) : new Set())
          }}
        />
      </>
    ),
    emptyTitle: (hasActiveFilters: boolean) =>
      hasActiveFilters ? 'No matching Invoices' : 'No Invoices yet',
  }
}

function IncomeDrillDownSummary({ drillDown }: { drillDown: IncomeInvoiceDrillDown }) {
  const invoiceLabel = drillDown.invoiceCount === 1 ? 'Invoice' : 'Invoices'
  const metric = drillDown.metric === 'discount' ? 'Discount' : capitalize(drillDown.metric)
  const summary = `${drillDown.label} · ${drillDown.invoiceCount} contributing ${invoiceLabel} · ${metric} ${drillDown.amount}`

  return (
    <p className="text-body-sm text-muted-foreground" aria-label={summary}>
      {drillDown.label} · {drillDown.invoiceCount} contributing {invoiceLabel} · {metric}{' '}
      <span className="font-medium text-foreground tabular-nums">{drillDown.amount}</span>
    </p>
  )
}

function capitalize(value: string) {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`
}

function InvoicesToolbar({
  filters,
  onChange,
}: {
  filters: InvoiceFilters
  onChange: React.Dispatch<React.SetStateAction<InvoiceFilters>>
}) {
  return (
    <>
      <Select
        value={filters.status}
        onValueChange={(status) =>
          onChange((current) => ({ ...current, status: status as InvoiceStatusFilter }))
        }
      >
        <SelectTrigger aria-label="Status" className="w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All statuses</SelectItem>
          <SelectItem value="Unpaid">Unpaid</SelectItem>
          <SelectItem value="Partially Paid">Partially Paid</SelectItem>
          <SelectItem value="Paid">Paid</SelectItem>
          <SelectItem value="Voided">Voided</SelectItem>
        </SelectContent>
      </Select>
      <Select
        value={filters.origin}
        onValueChange={(origin) =>
          onChange((current) => ({ ...current, origin: origin as InvoiceOriginFilter }))
        }
      >
        <SelectTrigger aria-label="Origin" className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All origins</SelectItem>
          {INVOICE_ORIGINS.map((origin) => (
            <SelectItem key={origin} value={origin}>
              {origin}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        aria-label="Issued from"
        type="date"
        className="w-40"
        value={filters.issuedFrom}
        onChange={(event) =>
          onChange((current) => ({ ...current, issuedFrom: event.target.value }))
        }
      />
      <Input
        aria-label="Issued through"
        type="date"
        className="w-40"
        value={filters.issuedThrough}
        onChange={(event) =>
          onChange((current) => ({ ...current, issuedThrough: event.target.value }))
        }
      />
      <label
        htmlFor="invoice-open-balance"
        className="flex items-center gap-2 text-body-sm text-foreground"
      >
        <Checkbox
          id="invoice-open-balance"
          checked={filters.openBalance}
          onCheckedChange={(checked) =>
            onChange((current) => ({ ...current, openBalance: checked === true }))
          }
        />
        Open Balance
      </label>
    </>
  )
}
